import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Sparkles, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("benjamin@rubi.com");
  const [password, setPassword] = useState("rubi2026");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - black/gold artistic */}
      <div className="hidden md:flex flex-1 relative overflow-hidden bg-[#0A0A0A]">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="aura-logo-mark w-12 h-12 rounded-2xl flex items-center justify-center font-display text-white font-bold text-2xl">
              A
            </div>
            <div>
              <div className="font-display text-white text-xl font-medium leading-none">
                Aura Rubí OS
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">
                Social AI Command Center
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="label-overline text-[#D99D26]/80 mb-4">
              <Sparkles size={14} className="inline mr-2" /> Bienvenido al
              sistema operativo creativo
            </div>
            <h2 className="font-display text-white text-4xl lg:text-5xl font-light leading-[1.1] tracking-tight">
              La máquina creativa de{" "}
              <span className="text-[#D99D26]">Rubí Perfumería</span>.
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed text-base">
              Estrategia, contenido, calendario, métricas y aprendizaje continuo,
              orquestado por Aura Marketing AI.
            </p>
          </div>

          <div className="flex gap-8 text-white/40">
            <div>
              <div className="font-display text-2xl text-white">15+</div>
              <div className="text-[10px] tracking-[0.2em] uppercase">Módulos</div>
            </div>
            <div>
              <div className="font-display text-2xl text-white">22</div>
              <div className="text-[10px] tracking-[0.2em] uppercase">Roles</div>
            </div>
            <div>
              <div className="font-display text-2xl text-white">∞</div>
              <div className="text-[10px] tracking-[0.2em] uppercase">Ideas</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="w-full md:w-[440px] bg-white flex flex-col justify-center px-8 py-10">
        <div className="max-w-sm w-full mx-auto">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="aura-logo-mark w-10 h-10 rounded-xl flex items-center justify-center font-display text-white font-bold">
              A
            </div>
            <div className="font-display text-lg font-medium">Aura Rubí OS</div>
          </div>

          <div className="label-overline mb-2">Acceso</div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Inicia sesión
          </h1>
          <p className="text-[#666] mt-2 mb-8 text-sm">
            Entra a tu command center creativo.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="aura-label">Correo</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="aura-input"
                placeholder="benjamin@rubi.com"
              />
            </div>
            <div>
              <label className="aura-label">Contraseña</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="aura-input pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#0A0A0A]"
                  data-testid="toggle-password"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="btn-primary w-full justify-center flex items-center gap-2"
            >
              {loading && <span className="spinner" />}
              {loading ? "Ingresando..." : "Ingresar"}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[#E5E5E5]">
            <div className="label-overline mb-3">Cuentas demo</div>
            <div className="space-y-2 text-xs text-[#666]">
              <div className="flex justify-between">
                <span className="font-medium text-[#0A0A0A]">CEO Benjamín</span>
                <span className="font-mono">benjamin@rubi.com</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#0A0A0A]">Director Marketing</span>
                <span className="font-mono">director@rubi.com</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[#0A0A0A]">Contraseña</span>
                <span className="font-mono">rubi2026</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
